/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { CartItem } from './CartContext';
import type { LocalizedText } from '../i18n/locale';
import { buildItemPhotoEndpoint } from '../api';
import {
  normalizeColorHex,
  normalizeFabricBlendLabel,
  normalizeFitLabel,
  normalizeGenderLabel,
  normalizeMaterialRecord,
  normalizeMeasurementsRecord,
  normalizeSizeValue,
} from '../features/cartItemConfiguration';
import {
  normalizeDeliveryEvents,
  type DeliveryEvent,
  type DeliveryEventServiceArea,
} from '../features/deliveryEvents';
import {
  AUTH_FLAG_KEY,
  AUTH_STATE_CHANGE_EVENT,
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  getGuestSessionId,
  fetchAwsApi,
  getApiErrorMessage,
  getSessionAccountId,
  isAuthenticatedSession,
  parseApiResponse,
  parseJsonValue,
  sanitizeErrorMessage,
} from '../security';

type UnknownRecord = Record<string, unknown>;
type RemoteOrderPhoto = UnknownRecord;

export type OrderItemStatus =
  | 'pending'
  | 'ordered'
  | 'processing'
  | 'shipped'
  | 'delivered'
  | 'cancelled'
  | 'refunded';

export type OrderDeliveryServiceArea = DeliveryEventServiceArea;
export type OrderDeliveryEvent = DeliveryEvent;

export interface OrderItem {
  id: string;
  itemId?: string;
  cadFileId?: string;
  carrier: string;
  trackingNumber: string;
  isActive?: boolean;
  s3Key?: string;
  measurements?: CartItem['measurements'];
  size?: string;
  fit?: string;
  color?: string;
  fabricBlend?: string;
  material?: CartItem['material'];
  gender?: string;
  deletedAt?: string;
  title: string;
  localizedTitle?: LocalizedText;
  description: string;
  localizedDescription?: LocalizedText;
  image: string;
  details?: Record<string, string[]>;
  quantity: number;
  unitPrice: number;
  status: OrderItemStatus;
  type: CartItem['type'];
  estimatedDeliveryDate?: string;
  deliveryEvents: OrderDeliveryEvent[];
}

export interface Order {
  id: string;
  accountId: string;
  createdAt: string;
  updatedAt: string;
  subtotal: number;
  taxAmount: number;
  shippingCost: number;
  totalAmount: number;
  paymentStatus: string;
  paymentIntentId: string;
  items: OrderItem[];
}

interface OrderHistoryContextType {
  activeAccountId: string;
  orders: Order[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string;
  refreshOrders: (filters?: { startDate?: string; endDate?: string }) => Promise<void>;
  loadMoreOrders: () => Promise<void>;
  deleteOrder: (orderId: string) => Promise<void>;
  saveSubmittedOrder: (remoteOrder: unknown, cartItems: CartItem[]) => Order | null;
}

type RemoteOrderItem = UnknownRecord;
const ORDER_ENDPOINT = '/api/add_order';
const REMOVE_ORDER_ENDPOINT = '/api/remove_order';
const DEFAULT_ORDER_PAGE_SIZE = 10;
const ORDER_PAGE_SIZE = (() => {
  const parsedSize = Number.parseInt(__ORDER_PAGE_SIZE__.trim(), 10);
  return Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : DEFAULT_ORDER_PAGE_SIZE;
})();

let clientIpPromise: Promise<string> | null = null;

async function getClientIpForOrderHistory() {
  if (typeof window === 'undefined') {
    return '';
  }

  if (!clientIpPromise) {
    clientIpPromise = (async () => {
      const controller = new AbortController();
      const timeoutId = window.setTimeout(() => controller.abort(), 1500);

      try {
        const response = await fetch('https://api64.ipify.org?format=json', {
          headers: { Accept: 'application/json' },
          signal: controller.signal,
        });
        if (!response.ok) {
          return '';
        }

        const payload = await response.json() as { ip?: unknown };
        const ip = typeof payload.ip === 'string' ? payload.ip.trim() : '';
        return /^[0-9a-fA-F:.]+$/.test(ip) ? ip : '';
      } catch {
        return '';
      } finally {
        window.clearTimeout(timeoutId);
      }
    })();
  }

  return clientIpPromise;
}

const OrderHistoryContext = createContext<OrderHistoryContextType | undefined>(undefined);

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getActiveOrderAccountId() {
  return getSessionAccountId();
}

function normalizeMoney(value: unknown) {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : 0;
}

function normalizeString(value: unknown, fallback = '') {
  return typeof value === 'string' ? value.trim() : fallback;
}

function formatUtcDateInputValue(date: Date) {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function toExclusiveEndDate(endDate: string) {
  const normalizedEndDate = normalizeString(endDate);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalizedEndDate)) {
    return normalizedEndDate;
  }

  const [year, month, day] = normalizedEndDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  if (Number.isNaN(date.getTime())) {
    return normalizedEndDate;
  }

  date.setUTCDate(date.getUTCDate() + 1);
  return formatUtcDateInputValue(date);
}

function normalizeOrderColorValue(...values: unknown[]) {
  for (const value of values) {
    const normalizedHex = normalizeColorHex(value);
    if (normalizedHex) {
      return normalizedHex;
    }

    const normalizedValue = normalizeString(value).slice(0, 80);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return undefined;
}

function normalizeDateString(value: unknown) {
  if (typeof value !== 'string') {
    return new Date().toISOString();
  }

  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : new Date().toISOString();
}

function normalizeOptionalTimestamp(value: unknown) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const timestamp = new Date(value);
    return Number.isNaN(timestamp.getTime()) ? undefined : timestamp.toISOString();
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const normalizedValue = value.trim();
  if (!normalizedValue) {
    return undefined;
  }

  const timestamp = Date.parse(normalizedValue);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : undefined;
}

function normalizeOptionalBoolean(value: unknown) {
  return typeof value === 'boolean' ? value : undefined;
}

function formatDateInputValue(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getDefaultOrderFilterStartDate() {
  const defaultDate = new Date();
  defaultDate.setMonth(defaultDate.getMonth() - 1);
  return formatDateInputValue(defaultDate);
}

export function getDefaultOrderFilterEndDate() {
  return formatDateInputValue(new Date());
}

function normalizeFiniteNumber(value: unknown) {
  const numericValue = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function normalizeNonNegativeInteger(value: unknown) {
  const numericValue = normalizeFiniteNumber(value);
  if (numericValue === null) {
    return null;
  }

  const normalizedValue = Math.max(Math.trunc(numericValue), 0);
  return Number.isFinite(normalizedValue) ? normalizedValue : null;
}

function resolveHasMoreOrders(
  payload: unknown,
  pageResponseCount: number,
  requestOffset: number,
  requestLimit: number
) {
  if (pageResponseCount <= 0) {
    return false;
  }

  if (isRecord(payload)) {
    const pagination = isRecord(payload.pagination) ? payload.pagination : null;
    const totalCandidates = [
      pagination?.total,
      payload.total,
    ];
    const total = totalCandidates
      .map((value) => normalizeNonNegativeInteger(value))
      .find((value): value is number => value !== null);
    if (total !== undefined) {
      const responseOffset = normalizeNonNegativeInteger(pagination?.offset) ?? requestOffset;
      const responseCount = normalizeNonNegativeInteger(pagination?.count) ?? pageResponseCount;
      return responseOffset + responseCount < total;
    }

    const nextOffsetCandidates = [
      pagination?.next_offset,
      pagination?.nextOffset,
      payload.next_offset,
      payload.nextOffset,
    ];
    const nextOffset = nextOffsetCandidates
      .map((value) => normalizeNonNegativeInteger(value))
      .find((value): value is number => value !== null);
    if (nextOffset !== undefined) {
      return nextOffset > requestOffset;
    }

    const hasMoreCandidates = [
      payload.has_more,
      payload.hasMore,
      payload.more,
      payload.has_next,
      payload.hasNext,
      pagination?.has_more,
      pagination?.hasMore,
      pagination?.more,
      pagination?.has_next,
      pagination?.hasNext,
    ];

    for (const candidate of hasMoreCandidates) {
      if (typeof candidate === 'boolean') {
        return candidate;
      }
    }
  }

  return pageResponseCount >= requestLimit;
}

function normalizeOrderItemStatus(value: unknown): OrderItemStatus {
  switch (typeof value === 'string' ? value.trim().toLowerCase() : '') {
    case 'pending':
      return 'pending';
    case 'ordered':
      return 'ordered';
    case 'processing':
      return 'processing';
    case 'shipped':
      return 'shipped';
    case 'delivered':
      return 'delivered';
    case 'cancelled':
      return 'cancelled';
    case 'refunded':
      return 'refunded';
    default:
      return 'ordered';
  }
}

function normalizeOrderType(value: unknown): CartItem['type'] {
  switch (value) {
    case 'hairstyle':
    case 'clothing':
    case 'nails':
      return value;
    default:
      return 'clothing';
  }
}

function inferOrderType(
  category: unknown,
  itemSubcategory: unknown,
  itemSubcategory2: unknown,
  fallbackType?: CartItem['type']
) {
  const candidates = [category, itemSubcategory, itemSubcategory2]
    .filter((value): value is string => typeof value === 'string')
    .map((value) => value.trim().toLowerCase());

  if (candidates.some((value) => value.includes('hair'))) {
    return 'hairstyle';
  }

  if (candidates.some((value) => value.includes('nail') || value.includes('makeup'))) {
    return 'nails';
  }

  return fallbackType ?? 'clothing';
}

function parseItemDetails(value: unknown) {
  if (isRecord(value)) {
    return value;
  }

  if (typeof value !== 'string') {
    return null;
  }

  const parsedValue = parseJsonValue(value);
  return isRecord(parsedValue) ? parsedValue : null;
}

function pickFirstString(...values: unknown[]) {
  for (const value of values) {
    const normalizedValue = normalizeString(value);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return '';
}

function normalizeObjectKeyCandidate(value: string) {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      const queryObjectKey = parsed.searchParams.get('objectKey') ?? parsed.searchParams.get('s3_key');
      if (queryObjectKey) {
        return decodeURIComponent(queryObjectKey).replace(/^\/+/, '');
      }
      return decodeURIComponent(parsed.pathname).replace(/^\/+/, '');
    } catch {
      return '';
    }
  }

  return trimmed.replace(/^\/+/, '');
}

function parseRecordList(value: unknown) {
  if (Array.isArray(value)) {
    return value.filter((entry): entry is UnknownRecord => isRecord(entry));
  }

  if (typeof value === 'string') {
    const parsedValue = parseJsonValue(value);
    if (Array.isArray(parsedValue)) {
      return parsedValue.filter((entry): entry is UnknownRecord => isRecord(entry));
    }
  }

  return [];
}

function parseOrderItemsList(value: unknown): unknown[] {
  if (Array.isArray(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const parsedValue = parseJsonValue(value);
    if (Array.isArray(parsedValue)) {
      return parsedValue;
    }
  }

  return [];
}

function extractOrderItemPhotoObjectKey(photo: RemoteOrderPhoto) {
  const directCandidates = [photo.s3_key, photo.s3Key, photo.objectKey];
  for (const candidate of directCandidates) {
    if (typeof candidate !== 'string') {
      continue;
    }

    const normalized = normalizeObjectKeyCandidate(candidate);
    if (normalized) {
      return normalized;
    }
  }

  return '';
}

function getOrderItemPhotoObjectKeyFromList(value: unknown) {
  const photos = parseRecordList(value)
    .sort((a, b) => {
      const aSortOrder = Number(a.sort_order ?? a.sortOrder ?? 0);
      const bSortOrder = Number(b.sort_order ?? b.sortOrder ?? 0);
      return aSortOrder - bSortOrder;
    });
  if (photos.length === 0) {
    return '';
  }

  const primaryPhoto = photos.find((photo) => photo.is_primary === true || photo.isPrimary === true) ?? photos[0];
  return primaryPhoto ? extractOrderItemPhotoObjectKey(primaryPhoto) : '';
}

function normalizeColorMatchToken(value: unknown) {
  const normalizedHex = normalizeColorHex(value);
  if (normalizedHex) {
    return normalizedHex.toLowerCase();
  }

  const normalizedText = normalizeString(value)
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return normalizedText;
}

function collectColorMatchTokens(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((entry) => collectColorMatchTokens(entry));
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const token = normalizeColorMatchToken(String(value));
    return token ? [token] : [];
  }

  if (!isRecord(value)) {
    return [];
  }

  const directTokens = [
    value.color,
    value.color_code,
    value.colorCode,
    value.color_name,
    value.colorName,
    value.hex,
    value.hex_code,
    value.hexCode,
    value.color_hex,
    value.colorHex,
    value.code,
    value.value,
    value.name,
  ];
  const labelTokens = isRecord(value.label)
    ? [value.label.en, value.label.zh]
    : [value.label];

  return [...directTokens, ...labelTokens]
    .map((candidate) => normalizeColorMatchToken(candidate))
    .filter(Boolean);
}

function getOrderedColorMatchTokens(
  item: RemoteOrderItem,
  itemDetails: UnknownRecord | null,
  fallbackCartItem?: CartItem
) {
  const selectedOptions = [
    item.selectedOptions,
    item.selected_options,
    itemDetails?.selectedOptions,
    itemDetails?.selected_options,
  ].find(isRecord);
  const details = isRecord(item.details) ? item.details : null;
  const itemDetailsDetails = isRecord(itemDetails?.details) ? itemDetails.details : null;

  return Array.from(new Set([
    ...collectColorMatchTokens(item.color),
    ...collectColorMatchTokens(item.color_code),
    ...collectColorMatchTokens(item.colorCode),
    ...collectColorMatchTokens(itemDetails?.color),
    ...collectColorMatchTokens(itemDetails?.color_code),
    ...collectColorMatchTokens(itemDetails?.colorCode),
    ...collectColorMatchTokens(selectedOptions?.color),
    ...collectColorMatchTokens(details?.Color ?? details?.color),
    ...collectColorMatchTokens(itemDetailsDetails?.Color ?? itemDetailsDetails?.color),
    ...collectColorMatchTokens(fallbackCartItem?.color),
  ]));
}

function getInventoryColorMatchTokens(inventoryRow: UnknownRecord) {
  return Array.from(new Set([
    ...collectColorMatchTokens(inventoryRow),
    ...collectColorMatchTokens(inventoryRow.color),
    ...collectColorMatchTokens(inventoryRow.color_code),
    ...collectColorMatchTokens(inventoryRow.colorCode),
    ...collectColorMatchTokens(inventoryRow.color_name),
    ...collectColorMatchTokens(inventoryRow.colorName),
  ]));
}

function getInventoryPhotoObjectKey(inventoryRow: UnknownRecord) {
  const nestedObjectKey =
    getOrderItemPhotoObjectKeyFromList(inventoryRow.s3_keys)
    || getOrderItemPhotoObjectKeyFromList(inventoryRow.s3Keys)
    || getOrderItemPhotoObjectKeyFromList(inventoryRow.photos)
    || getOrderItemPhotoObjectKeyFromList(inventoryRow.item_photos)
    || getOrderItemPhotoObjectKeyFromList(inventoryRow.itemPhotos);

  if (nestedObjectKey) {
    return nestedObjectKey;
  }

  return extractOrderItemPhotoObjectKey(inventoryRow);
}

function getOrderItemInventoryPreviewUrl(
  item: RemoteOrderItem,
  itemDetails: UnknownRecord | null,
  fallbackCartItem?: CartItem
) {
  const inventoryRows = [
    ...parseRecordList(item.inventory),
    ...parseRecordList(item.inventory_rows),
    ...parseRecordList(item.inventoryRows),
    ...parseRecordList(itemDetails?.inventory),
    ...parseRecordList(itemDetails?.inventory_rows),
    ...parseRecordList(itemDetails?.inventoryRows),
  ];
  if (inventoryRows.length === 0) {
    return '';
  }

  const orderedColorTokens = getOrderedColorMatchTokens(item, itemDetails, fallbackCartItem);
  const matchingInventoryRow = orderedColorTokens.length > 0
    ? inventoryRows.find((inventoryRow) => {
      const inventoryColorTokens = getInventoryColorMatchTokens(inventoryRow);
      return inventoryColorTokens.some((token) => orderedColorTokens.includes(token));
    })
    : undefined;
  const inventoryRow = matchingInventoryRow ?? inventoryRows[0];
  const objectKey = getInventoryPhotoObjectKey(inventoryRow);

  return objectKey ? buildItemPhotoEndpoint(objectKey) : '';
}

function getOrderItemPhotoPreviewUrl(item: RemoteOrderItem, itemDetails: UnknownRecord | null) {
  const photoCandidates = [
    item.photos,
    item.item_photos,
    item.itemPhotos,
    itemDetails?.photos,
    itemDetails?.item_photos,
    itemDetails?.itemPhotos,
  ];

  for (const candidate of photoCandidates) {
    const photos = parseRecordList(candidate);
    if (photos.length === 0) {
      continue;
    }

    const primaryPhoto = photos.find((photo) => photo.is_primary === true || photo.isPrimary === true) ?? photos[0];
    if (!primaryPhoto) {
      continue;
    }

    const objectKey = extractOrderItemPhotoObjectKey(primaryPhoto);
    if (objectKey) {
      return buildItemPhotoEndpoint(objectKey);
    }
  }

  return '';
}

function normalizeLocalizedText(value: unknown): LocalizedText | undefined {
  if (!isRecord(value)) {
    return undefined;
  }

  const en = normalizeString(value.en);
  const zh = normalizeString(value.zh);
  const ja = normalizeString(value.ja);
  const jp = normalizeString(value.jp);
  const es = normalizeString(value.es);
  const fr = normalizeString(value.fr);
  const ru = normalizeString(value.ru);

  if (!en && !zh && !ja && !jp && !es && !fr && !ru) {
    return undefined;
  }

  return {
    ...(en ? { en } : {}),
    ...(zh ? { zh } : {}),
    ...(ja ? { ja } : {}),
    ...(jp ? { jp } : {}),
    ...(es ? { es } : {}),
    ...(fr ? { fr } : {}),
    ...(ru ? { ru } : {}),
  };
}

function pickFirstLocalizedText(...values: unknown[]) {
  for (const value of values) {
    const normalizedValue = normalizeLocalizedText(value);
    if (normalizedValue) {
      return normalizedValue;
    }
  }

  return undefined;
}

function normalizeDetailLabel(value: unknown) {
  const normalizedValue = normalizeString(value);
  return normalizedValue ? normalizedValue.slice(0, 40) : '';
}

function normalizeDetailValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return Array.from(new Set(value
      .map((entry) => normalizeString(entry))
      .filter(Boolean)))
      .slice(0, 6);
  }

  if (typeof value === 'string' || typeof value === 'number') {
    const normalizedValue = normalizeString(String(value));
    return normalizedValue ? [normalizedValue] : [];
  }

  return [];
}

function appendDetail(
  details: Record<string, string[]>,
  label: string,
  rawValue: unknown
) {
  const normalizedLabel = normalizeDetailLabel(label);
  const normalizedValues = normalizeDetailValues(rawValue);

  if (!normalizedLabel || normalizedValues.length === 0) {
    return;
  }

  details[normalizedLabel] = Array.from(new Set([
    ...(details[normalizedLabel] ?? []),
    ...normalizedValues,
  ])).slice(0, 6);
}

function appendRecordDetails(
  details: Record<string, string[]>,
  value: unknown
) {
  if (!isRecord(value)) {
    return;
  }

  Object.entries(value).forEach(([key, entryValue]) => {
    appendDetail(details, key, entryValue);
  });
}

function buildOrderItemDetails(item: RemoteOrderItem, itemDetails: UnknownRecord | null, fallbackCartItem?: CartItem) {
  const details: Record<string, string[]> = {};

  appendRecordDetails(details, item.details);
  appendRecordDetails(details, itemDetails?.details);
  appendRecordDetails(details, item.options);
  appendRecordDetails(details, item.item_options ?? item.itemOptions);
  appendRecordDetails(details, itemDetails?.options);
  appendRecordDetails(details, itemDetails?.selectedOptions);
  appendRecordDetails(details, fallbackCartItem?.details);

  appendDetail(details, 'Size', item.size ?? itemDetails?.size ?? fallbackCartItem?.size);
  appendDetail(details, 'Fit', item.fit ?? itemDetails?.fit ?? fallbackCartItem?.fit);
  appendDetail(
    details,
    'Fabric',
    item.fabric_blend ?? item.fabricBlend ?? itemDetails?.fabric_blend ?? itemDetails?.fabricBlend ?? fallbackCartItem?.fabricBlend
  );
  appendDetail(details, 'Gender', item.gender ?? itemDetails?.gender ?? fallbackCartItem?.gender);
  appendDetail(details, 'Color', item.color ?? itemDetails?.color ?? fallbackCartItem?.color);
  appendDetail(details, 'Style', item.style ?? itemDetails?.style);

  return Object.keys(details).length > 0 ? details : undefined;
}

function getOrderItemFallbackTitle(item: RemoteOrderItem) {
  const itemId = normalizeString(item.item_id ?? item.itemId);
  const cadFileId = normalizeString(item.cad_file_id ?? item.cadFileId);

  if (itemId) {
    return `Store Item ${itemId.slice(0, 8)}`;
  }

  if (cadFileId) {
    return `Custom Design ${cadFileId.slice(0, 8)}`;
  }

  return 'Custom Order Item';
}

function getOrderItemFallbackDescription(item: RemoteOrderItem) {
  const itemId = normalizeString(item.item_id ?? item.itemId);
  const cadFileId = normalizeString(item.cad_file_id ?? item.cadFileId);

  if (itemId) {
    return 'Saved from your VADAURO store catalog.';
  }

  if (cadFileId) {
    return 'Created from one of your saved custom design files.';
  }

  return 'Saved as part of your previous VADAURO order.';
}

function normalizeOrderItem(item: unknown, fallbackCartItem?: CartItem): OrderItem | null {
  if (!isRecord(item)) {
    return null;
  }

  const itemDetails = parseItemDetails(item.item_details ?? item.itemDetails);
  const orderItemId = normalizeString(item.id) || `item-${Date.now()}`;
  const quantity = Number(item.quantity);
  const unitPrice = normalizeMoney(item.unit_price ?? item.unitPrice);
  const localizedTitle = pickFirstLocalizedText(item.title, itemDetails?.title);
  const localizedDescription = pickFirstLocalizedText(item.description, itemDetails?.description);
  const previewFromInventory = getOrderItemInventoryPreviewUrl(item, itemDetails, fallbackCartItem);
  const previewFromPhotos = getOrderItemPhotoPreviewUrl(item, itemDetails);

  if (!Number.isInteger(quantity) || quantity < 1 || unitPrice <= 0) {
    return null;
  }

  return {
    id: orderItemId,
    itemId: pickFirstString(item.item_id, item.itemId, itemDetails?.item_id, itemDetails?.itemId, fallbackCartItem?.itemId) || undefined,
    cadFileId:
      pickFirstString(
        item.cad_file_id,
        item.cadFileId,
        itemDetails?.cad_file_id,
        itemDetails?.cadFileId,
        fallbackCartItem?.cadFileId
      ) || undefined,
    carrier: normalizeString(item.carrier),
    trackingNumber: normalizeString(item.tracking_number ?? item.trackingNumber),
    isActive:
      normalizeOptionalBoolean(item.is_active)
      ?? normalizeOptionalBoolean(item.isActive)
      ?? normalizeOptionalBoolean(itemDetails?.is_active)
      ?? normalizeOptionalBoolean(itemDetails?.isActive)
      ?? true,
    s3Key:
      pickFirstString(
        item.s3_key,
        item.s3Key,
        itemDetails?.s3_key,
        itemDetails?.s3Key,
        fallbackCartItem?.s3Key
      ) || undefined,
    measurements:
      normalizeMeasurementsRecord(item.measurements)
      ?? normalizeMeasurementsRecord(itemDetails?.measurements)
      ?? fallbackCartItem?.measurements,
    size:
      normalizeSizeValue(item.size)
      ?? normalizeSizeValue(itemDetails?.size)
      ?? fallbackCartItem?.size,
    fit:
      normalizeFitLabel(item.fit)
      ?? normalizeFitLabel(itemDetails?.fit)
      ?? fallbackCartItem?.fit,
    color:
      normalizeOrderColorValue(item.color, itemDetails?.color, fallbackCartItem?.color),
    fabricBlend:
      normalizeFabricBlendLabel(item.fabric_blend ?? item.fabricBlend)
      ?? normalizeFabricBlendLabel(itemDetails?.fabric_blend ?? itemDetails?.fabricBlend)
      ?? fallbackCartItem?.fabricBlend,
    material:
      normalizeMaterialRecord(item.material)
      ?? normalizeMaterialRecord(itemDetails?.material)
      ?? fallbackCartItem?.material,
    gender:
      normalizeGenderLabel(item.gender)
      ?? normalizeGenderLabel(itemDetails?.gender)
      ?? fallbackCartItem?.gender,
    deletedAt: normalizeString(item.deleted_at ?? item.deletedAt) || undefined,
    title:
      pickFirstString(
        item.title,
        itemDetails?.title,
        fallbackCartItem?.title,
        localizedTitle?.en,
        localizedTitle?.zh,
        localizedTitle?.ja,
        localizedTitle?.jp,
        localizedTitle?.es,
        localizedTitle?.fr,
        localizedTitle?.ru,
      )
      || getOrderItemFallbackTitle(item),
    localizedTitle,
    description:
      pickFirstString(
        item.description,
        itemDetails?.description,
        fallbackCartItem?.description,
        localizedDescription?.en,
        localizedDescription?.zh,
        localizedDescription?.ja,
        localizedDescription?.jp,
        localizedDescription?.es,
        localizedDescription?.fr,
        localizedDescription?.ru,
      )
      || getOrderItemFallbackDescription(item),
    localizedDescription,
    image:
      pickFirstString(
        previewFromInventory,
        previewFromPhotos,
        item.image,
        item.image_url,
        item.imageUrl,
        item.thumbnail,
        item.thumbnail_url,
        item.thumbnailUrl,
        itemDetails?.image,
        itemDetails?.image_url,
        itemDetails?.imageUrl,
        itemDetails?.thumbnail,
        itemDetails?.thumbnail_url,
        itemDetails?.thumbnailUrl,
        fallbackCartItem?.image
      ),
    details: buildOrderItemDetails(item, itemDetails, fallbackCartItem),
    quantity,
    unitPrice,
    status: normalizeOrderItemStatus(item.order_status ?? item.status),
    type: inferOrderType(
      item.category ?? itemDetails?.category,
      item.item_subcategory ?? item.itemSubcategory ?? itemDetails?.item_subcategory ?? itemDetails?.itemSubcategory,
      item.item_subcategory2 ?? item.itemSubcategory2 ?? itemDetails?.item_subcategory2 ?? itemDetails?.itemSubcategory2,
      normalizeOrderType(item.type ?? itemDetails?.type ?? fallbackCartItem?.type)
    ),
    estimatedDeliveryDate: normalizeOptionalTimestamp(
      item.estimated_delivery_date
      ?? item.estimatedDeliveryDate
      ?? itemDetails?.estimated_delivery_date
      ?? itemDetails?.estimatedDeliveryDate
    ),
    deliveryEvents: normalizeDeliveryEvents(
      item.delivery_events
      ?? item.deliveryEvents
      ?? itemDetails?.delivery_events
      ?? itemDetails?.deliveryEvents
    ),
  };
}

function normalizeFallbackCartItem(item: CartItem): OrderItem | null {
  if (!item.id || !item.title || item.quantity < 1 || item.price <= 0) {
    return null;
  }

  return {
    id: item.id,
    itemId: item.itemId,
    cadFileId: item.cadFileId,
    carrier: '',
    trackingNumber: '',
    isActive: true,
    s3Key: item.s3Key,
    measurements: item.measurements,
    size: item.size,
    fit: item.fit,
    color: item.color,
    fabricBlend: item.fabricBlend,
    material: item.material,
    gender: item.gender,
    deletedAt: undefined,
    title: item.title,
    localizedTitle: undefined,
    description: item.description,
    localizedDescription: undefined,
    image: item.image,
    details: item.details,
    quantity: item.quantity,
    unitPrice: normalizeMoney(item.price),
    status: 'ordered',
    type: item.type,
    estimatedDeliveryDate: undefined,
    deliveryEvents: [],
  };
}

function normalizeOrder(remoteOrder: unknown, fallbackCartItems: CartItem[] = []): Order | null {
  if (!isRecord(remoteOrder)) {
    return null;
  }

  const orderId = normalizeString(remoteOrder.id);
  if (!orderId) {
    return null;
  }

  const deletedAt = normalizeString(remoteOrder.deleted_at ?? remoteOrder.deletedAt);
  if (deletedAt) {
    return null;
  }

  const rawItems = (() => {
    const directItems = parseOrderItemsList(remoteOrder.items);
    if (directItems.length > 0) {
      return directItems;
    }

    const snakeCaseItems = parseOrderItemsList(remoteOrder.order_items);
    if (snakeCaseItems.length > 0) {
      return snakeCaseItems;
    }

    return parseOrderItemsList(remoteOrder.orderItems);
  })();

  const items = rawItems
    .map((item, index) => normalizeOrderItem(item, fallbackCartItems[index]))
    .filter((item): item is OrderItem => item !== null);
  const fallbackItems = fallbackCartItems
    .map((item) => normalizeFallbackCartItem(item))
    .filter((item): item is OrderItem => item !== null);
  const resolvedItems = items.length > 0 ? items : fallbackItems;

  if (resolvedItems.length === 0) {
    return null;
  }

  return {
    id: orderId,
    accountId: normalizeString(remoteOrder.account_id ?? remoteOrder.accountId),
    createdAt: normalizeDateString(remoteOrder.created_at ?? remoteOrder.createdAt),
    updatedAt: normalizeDateString(remoteOrder.updated_at ?? remoteOrder.updatedAt ?? remoteOrder.created_at),
    subtotal: normalizeMoney(remoteOrder.subtotal),
    taxAmount: normalizeMoney(remoteOrder.tax_amount ?? remoteOrder.taxAmount),
    shippingCost: normalizeMoney(remoteOrder.shipping_cost ?? remoteOrder.shippingCost),
    totalAmount: normalizeMoney(remoteOrder.total_price ?? remoteOrder.totalAmount),
    paymentStatus: normalizeString(remoteOrder.payment_status ?? remoteOrder.paymentStatus, 'pending') || 'pending',
    paymentIntentId: normalizeString(remoteOrder.payment_intent_id ?? remoteOrder.paymentIntentId),
    items: resolvedItems,
  };
}

export function OrderHistoryProvider({ children }: { children: React.ReactNode }) {
  const [activeAccountId, setActiveAccountId] = useState(() => getActiveOrderAccountId());
  const [orders, setOrders] = useState<Order[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState('');
  const [appliedFilters, setAppliedFilters] = useState<{ startDate?: string; endDate?: string }>({
    startDate: getDefaultOrderFilterStartDate(),
    endDate: getDefaultOrderFilterEndDate(),
  });
  const activeAccountIdRef = useRef(activeAccountId);
  const nextOrderOffsetRef = useRef(0);
  const appliedFiltersRef = useRef(appliedFilters);
  const latestOrderRequestIdRef = useRef(0);

  useEffect(() => {
    activeAccountIdRef.current = activeAccountId;
  }, [activeAccountId]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const syncOrdersFromAuth = () => {
      const nextAccountId = getActiveOrderAccountId();
      if (nextAccountId === activeAccountIdRef.current) {
        return;
      }

      activeAccountIdRef.current = nextAccountId;
      latestOrderRequestIdRef.current += 1;
      setActiveAccountId(nextAccountId);
      setOrders([]);
      nextOrderOffsetRef.current = 0;
      setHasMore(false);
      setError('');
      setIsLoadingMore(false);
    };

    const handleStorage = (event: StorageEvent) => {
      if (
        event.key === null ||
        event.key === AUTH_FLAG_KEY ||
        event.key === AUTH_TOKEN_KEY ||
        event.key === AUTH_USER_KEY
      ) {
        syncOrdersFromAuth();
      }
    };

    syncOrdersFromAuth();
    window.addEventListener(AUTH_STATE_CHANGE_EVENT, syncOrdersFromAuth);
    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, syncOrdersFromAuth);
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const fetchOrdersPage = useCallback(async ({
    filters,
    offset,
    append,
  }: {
    filters?: { startDate?: string; endDate?: string };
    offset: number;
    append: boolean;
  }) => {
    const requestId = latestOrderRequestIdRef.current + 1;
    latestOrderRequestIdRef.current = requestId;

    if (!activeAccountId) {
      setOrders([]);
      nextOrderOffsetRef.current = 0;
      setIsLoading(false);
      setIsLoadingMore(false);
      setHasMore(false);
      setError('');
      return;
    }

    if (append) {
      setIsLoadingMore(true);
    } else {
      nextOrderOffsetRef.current = 0;
      setIsLoading(true);
      setIsLoadingMore(false);
    }
    setError('');

    try {
      const guestSessionId = !isAuthenticatedSession() ? getGuestSessionId() : '';
      const clientIp = await getClientIpForOrderHistory();
      const requestPath = (() => {
        const params = new URLSearchParams();
        const startDate = normalizeString(filters?.startDate);
        const endDate = normalizeString(filters?.endDate);

        if (startDate) {
          params.set('start_date', startDate);
        }
        if (endDate) {
          params.set('end_date', toExclusiveEndDate(endDate));
        }
        params.set('limit', String(ORDER_PAGE_SIZE));
        params.set('offset', String(offset));
        if (clientIp) {
          params.set('user_ip', clientIp);
        }

        const query = params.toString();
        return query ? `${ORDER_ENDPOINT}?${query}` : ORDER_ENDPOINT;
      })();

      const response = await fetchAwsApi(requestPath, {
        method: 'GET',
        headers: guestSessionId
          ? { 'X-Session-ID': guestSessionId }
          : undefined,
      });
      const payload = await parseApiResponse(response);

      if (!response.ok) {
        throw new Error(getApiErrorMessage(payload, 'Unable to load your order history right now.'));
      }

      if (requestId !== latestOrderRequestIdRef.current) {
        return;
      }

      const remoteOrders = isRecord(payload) && Array.isArray(payload.orders) ? payload.orders : [];
      const pageOrders = remoteOrders
        .map((entry) => normalizeOrder(entry))
        .filter((entry): entry is Order => entry !== null);
      nextOrderOffsetRef.current = offset + remoteOrders.length;
      setOrders((currentOrders) => {
        if (!append) {
          return pageOrders;
        }

        const nextOrders = [...currentOrders];
        for (const order of pageOrders) {
          if (nextOrders.some((entry) => entry.id === order.id)) {
            continue;
          }
          nextOrders.push(order);
        }
        return nextOrders;
      });
      setHasMore(resolveHasMoreOrders(payload, remoteOrders.length, offset, ORDER_PAGE_SIZE));
    } catch (loadError) {
      if (requestId !== latestOrderRequestIdRef.current) {
        return;
      }

      if (!append) {
        setOrders([]);
      }
      setHasMore(false);
      setError(
        sanitizeErrorMessage(
          loadError instanceof Error ? loadError.message : loadError,
          'Unable to load your order history right now.'
        )
      );
    } finally {
      if (requestId === latestOrderRequestIdRef.current) {
        if (append) {
          setIsLoadingMore(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [activeAccountId]);

  const refreshOrders = useCallback(async (filters?: { startDate?: string; endDate?: string }) => {
    const normalizedFilters = filters
      ? {
        startDate: normalizeString(filters.startDate) || undefined,
        endDate: normalizeString(filters.endDate) || undefined,
      }
      : {
        startDate: normalizeString(appliedFiltersRef.current.startDate) || undefined,
        endDate: normalizeString(appliedFiltersRef.current.endDate) || undefined,
      };
    setAppliedFilters(normalizedFilters);
    await fetchOrdersPage({ filters: normalizedFilters, offset: 0, append: false });
  }, [fetchOrdersPage]);

  const loadMoreOrders = useCallback(async () => {
    if (!activeAccountId || isLoading || isLoadingMore || !hasMore) {
      return;
    }

    await fetchOrdersPage({ filters: appliedFilters, offset: nextOrderOffsetRef.current, append: true });
  }, [activeAccountId, appliedFilters, fetchOrdersPage, hasMore, isLoading, isLoadingMore]);

  const saveSubmittedOrder = useCallback((remoteOrder: unknown, cartItems: CartItem[]) => {
    if (!isRecord(remoteOrder)) {
      return null;
    }
    const normalizedOrder = normalizeOrder(remoteOrder, cartItems);
    if (!normalizedOrder) {
      return null;
    }

    setOrders((currentOrders) => [
      normalizedOrder,
      ...currentOrders.filter((order) => order.id !== normalizedOrder.id),
    ]);
    return normalizedOrder;
  }, []);

  const deleteOrder = useCallback(async (orderId: string) => {
    const normalizedOrderId = normalizeString(orderId);
    if (!normalizedOrderId) {
      throw new Error('Missing order id.');
    }

    const guestSessionId = !isAuthenticatedSession() ? getGuestSessionId() : '';
    const response = await fetchAwsApi(`${REMOVE_ORDER_ENDPOINT}?order_id=${encodeURIComponent(normalizedOrderId)}`, {
      method: 'DELETE',
      headers: guestSessionId
        ? { 'X-Session-ID': guestSessionId }
        : undefined,
    });
    const payload = await parseApiResponse(response);

    if (!response.ok) {
      throw new Error(getApiErrorMessage(payload, 'Unable to delete this order right now.'));
    }

    setOrders((currentOrders) => currentOrders.filter((order) => order.id !== normalizedOrderId));
  }, []);

  const value = useMemo(
    () => ({
      activeAccountId,
      orders,
      isLoading,
      isLoadingMore,
      hasMore,
      error,
      refreshOrders,
      loadMoreOrders,
      deleteOrder,
      saveSubmittedOrder,
    }),
    [activeAccountId, orders, isLoading, isLoadingMore, hasMore, error, refreshOrders, loadMoreOrders, deleteOrder, saveSubmittedOrder]
  );

  return (
    <OrderHistoryContext.Provider value={value}>
      {children}
    </OrderHistoryContext.Provider>
  );
}

export function useOrderHistory() {
  const context = useContext(OrderHistoryContext);

  if (!context) {
    throw new Error('useOrderHistory must be used within an OrderHistoryProvider');
  }

  return context;
}
